export interface TimeSlot {
  open: TimeString;
  close: TimeString;
}
export type TimeString = `${number}:${number}`; // enforces "HH:MM" format

export interface Schedule {
  monday: TimeSlot[];
  tuesday: TimeSlot[];
  wednesday: TimeSlot[];
  thursday: TimeSlot[];
  friday: TimeSlot[];
  saturday: TimeSlot[];
  sunday: TimeSlot[];
}

export type ScheduleDayOfTheWeek = keyof Schedule;

export const DAYS_OF_WEEK: ScheduleDayOfTheWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export function isTimeSlot(value: unknown): value is TimeSlot {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  return (
    keys.length === 2 &&
    "open" in obj &&
    "close" in obj &&
    typeof obj.open === "string" &&
    typeof obj.close === "string"
  );
}

export function isSchedule(value: unknown): value is Schedule {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length !== DAYS_OF_WEEK.length) {
    return false;
  }
  return (
    DAYS_OF_WEEK.every(
      (day) =>
        Array.isArray(obj[day]) &&
        (obj[day].length === 0 || obj[day].every(isTimeSlot)),
    ) && keys.every((key) => DAYS_OF_WEEK.includes(key as ScheduleDayOfTheWeek))
  );
}

export const emptySchedule: Schedule = {
  monday: [],
  tuesday: [],
  wednesday: [],
  thursday: [],
  friday: [],
  saturday: [],
  sunday: [],
};

export function is24HourDay(slots: TimeSlot[] | undefined): boolean {
  if (!slots || slots.length !== 1) return false;
  const slot = slots[0];
  return slot.open === "00:00" && slot.close === "23:59";
}

export function isHoursEmpty(hours: Schedule | null | undefined): boolean {
  if (!hours) return true;
  return Object.values(hours).every(
    (slots) => !Array.isArray(slots) || slots.length === 0,
  );
}

export function mergeOvernightSlots(
  schedule: Schedule | null | undefined,
): Schedule | null {
  if (!schedule) return null;
  const normalizedSchedule: Schedule = JSON.parse(JSON.stringify(schedule));
  const daysOfTheWeek: ScheduleDayOfTheWeek[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  for (let i = 0; i < daysOfTheWeek.length; i++) {
    const currentDayName = daysOfTheWeek[i];
    const currentDaySlots = normalizedSchedule[currentDayName];
    if (!currentDaySlots || currentDaySlots.length === 0) {
      continue; // no slots for this day
    }
    const lastSlotOfTheDay = currentDaySlots[currentDaySlots.length - 1];
    const isCurrentDay24Hours = is24HourDay(currentDaySlots);
    // try to merge it into the next day if next day is not 24 hour day
    if (lastSlotOfTheDay.close === "23:59" && !isCurrentDay24Hours) {
      const nextDayName = daysOfTheWeek[(i + 1) % daysOfTheWeek.length];
      const nextDaySlots = normalizedSchedule[nextDayName];
      const isNextDay24Hours = is24HourDay(nextDaySlots);
      if (
        nextDaySlots &&
        nextDaySlots.length > 0 &&
        nextDaySlots[0].open === "00:00" &&
        !isNextDay24Hours
      ) {
        const slotToAbsorb = nextDaySlots.shift(); // remove slot from the next day
        lastSlotOfTheDay.close = slotToAbsorb?.close || "23:59"; // extend the current day's last slot to the end of the absorbed slot
      }
    }
  }
  return normalizedSchedule;
}

export function splitOvernightSlots(
  schedule: Schedule | null | undefined,
): Schedule | null {
  if (!schedule) return null;
  const newSchedule: Schedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  const daysOfTheWeek: ScheduleDayOfTheWeek[] = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];
  for (const [index, day] of daysOfTheWeek.entries()) {
    const slots = schedule[day];
    if (!Array.isArray(slots)) continue;
    for (const slot of slots) {
      if (slot.open && slot.close) {
        // Handle 24-hour slots
        if (is24HourDay([slot])) {
          newSchedule[day].push({
            open: "00:00",
            close: "23:59",
          });
          continue;
        }
        // if there's any slots where the start is before midnight and the end is after midnight,
        // we need to split them into two days
        if (isSlotSplitNeeded(slot)) {
          const firstSlot = {
            open: slot.open,
            close: "23:59",
          } as TimeSlot;
          const secondSlot = {
            open: "00:00",
            close: slot.close,
          } as TimeSlot;
          newSchedule[day].push(firstSlot);
          const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
          newSchedule[nextDay].push(secondSlot);
        } else {
          newSchedule[day].push({
            open: slot.open,
            close: slot.close,
          });
        }
      }
    }
  }
  Object.entries(newSchedule).forEach(([day, slots]) => {
    newSchedule[day as ScheduleDayOfTheWeek] = mergeSlots(slots);
  });
  return newSchedule;
}

function mergeSlots(slots: TimeSlot[]): TimeSlot[] {
  if (slots.length <= 1) return slots;

  // Sort slots by opening time
  const sortedSlots = [...slots].sort((a, b) => {
    if (!a.open || !b.open) return 0;
    return a.open.localeCompare(b.open);
  });

  const merged: TimeSlot[] = [];
  let current = sortedSlots[0];

  for (let i = 1; i < sortedSlots.length; i++) {
    const next = sortedSlots[i];

    if (!current.open || !current.close || !next.open || !next.close) continue;

    const currentClose = timeToMinutes(current.close);
    const nextOpen = timeToMinutes(next.open);
    const nextClose = timeToMinutes(next.close);

    // If slots overlap or are adjacent
    if (currentClose >= nextOpen) {
      // Merge by taking the later closing time
      current = {
        ...current,
        close: minutesToTime(Math.max(currentClose, nextClose)),
      } as TimeSlot;
    } else {
      merged.push(current);
      current = next;
    }
  }

  merged.push(current);
  return merged;
}

function timeToMinutes(time: TimeString): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

function minutesToTime(minutes: number): TimeString {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}` as TimeString;
}

function isSlotSplitNeeded(slot: TimeSlot): boolean {
  if (!slot.open || !slot.close) return false;
  const [hour1, min1] = slot.open.split(":").map(Number);
  const [hour2, min2] = slot.close.split(":").map(Number);
  return hour2 < hour1 || (hour2 === hour1 && min2 < min1);
}

// handles overnight slots too
export function timeSlotsOverlap(a: TimeSlot, b: TimeSlot): boolean {
  const expandSlot = (s: TimeSlot): Array<[string, string]> => {
    if (isSlotSplitNeeded(s)) {
      return [
        [s.open, "23:59"],
        ["00:00", s.close],
      ];
    }
    return [[s.open, s.close]];
  };
  const rangesA = expandSlot(a);
  const rangesB = expandSlot(b);
  for (const [aOpen, aClose] of rangesA) {
    for (const [bOpen, bClose] of rangesB) {
      if (aOpen < bClose && bOpen < aClose) return true;
    }
  }
  return false;
}

// null/undefined is treated as always available
export function schedulesOverlap(
  a: Schedule | null | undefined,
  b: Schedule | null | undefined,
): boolean {
  if (!a || !b) return true;
  for (const day of DAYS_OF_WEEK) {
    const slotsA = a[day];
    const slotsB = b[day];
    if (slotsA.length === 0 || slotsB.length === 0) continue;
    for (const slotA of slotsA) {
      for (const slotB of slotsB) {
        if (timeSlotsOverlap(slotA, slotB)) return true;
      }
    }
  }
  return false;
}
